import type { XoeOpenConversationDetail, OpenXoeConversationRequest } from '../../types/xoe/XoeUiTypes';
/**
 * @file Xoe widget "open on a conversation" bridge (S9 — Discuss with Xoe handoff).
 *
 * The XoeWidget owns its own open/closed state and the active conversation lives inside
 * the widget's XoeChat, so there is no prop path from an arbitrary page to "open Xoe on this
 * conversation". Rather than introduce a new singleton/store or a WebSocket, this uses a
 * one-shot `window` CustomEvent — the same decoupled pattern the app already uses for
 * theme changes (`window.dispatchEvent(new CustomEvent('themeChange', ...))` in App.tsx).
 *
 * Producer: a page calls openXoeConversation(uuid) after creating a conversation, and may
 * additionally pass `seedText` — the practitioner's question, phrased by the calling page —
 * so the widget's XoeChat runs the turn itself instead of the caller sending it directly (P8.4).
 * That keeps the transport choice (streaming vs synchronous) in exactly one place: the
 * widget's own dual-path `handleSend` / `runTurn` logic, correct in both flag states
 * automatically, rather than every producer page having to know which host to call.
 *
 * Consumer: XoeWidget subscribes, opens itself, and selects the requested conversation
 * (and, when present, hands the seed text to the widget's XoeChat to run as a turn).
 */

export const XOE_OPEN_CONVERSATION_EVENT = 'xoe:open-conversation';

/**
 * Ask the mounted XoeWidget to open (if closed) and focus the given conversation, optionally
 * running `seedText` as the first turn once it does.
 */
export const openXoeConversation = (uuid: string, seedText?: string): void => {
	if (typeof window === 'undefined' || !uuid) return;
	window.dispatchEvent(
		new CustomEvent<XoeOpenConversationDetail>(XOE_OPEN_CONVERSATION_EVENT, {
			detail: seedText !== undefined ? { uuid, seedText } : { uuid },
		}),
	);
};

/**
 * Subscribe to open-conversation requests. Returns an unsubscribe function.
 *
 * The callback receives the normalised request. A whitespace-only `seedText` is normalised
 * to `undefined` HERE — the trust boundary is the consumer, matching the existing
 * `typeof detail.uuid === 'string'` check below, not the producer.
 */
export const subscribeOpenXoeConversation = (
	callback: (request: OpenXoeConversationRequest) => void,
): (() => void) => {
	const handler = (event: Event) => {
		const detail = (event as CustomEvent<XoeOpenConversationDetail>).detail;
		if (detail && typeof detail.uuid === 'string' && detail.uuid) {
			const seedText =
				typeof detail.seedText === 'string' && detail.seedText.trim() !== ''
					? detail.seedText
					: undefined;
			callback({ uuid: detail.uuid, seedText });
		}
	};
	window.addEventListener(XOE_OPEN_CONVERSATION_EVENT, handler);
	return () => window.removeEventListener(XOE_OPEN_CONVERSATION_EVENT, handler);
};
